import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Check, Copy, Loader2, Trash2 } from "lucide-react";

import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const ConnectClaudeCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/mcp/connections");
      setData(res.data);
    } catch (e) {
      toast.error(errText(e) || "Could not read the Claude connections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const disconnect = async (clientId) => {
    try {
      await api.delete(`/mcp/connections/${clientId}`);
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(errText(e) || "Could not disconnect");
    }
  };

  const mcpUrl = data?.mcp_url || "";

  return (
    <section className="surface p-6 lg:p-8" data-testid="connect-claude-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <Bot className="h-5 w-5" /> Connect Claude
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Let Claude read your diary and file entries for you. Add this as a custom connector in Claude
        and sign in with your chambers Google account — Claude asks before it changes anything.
      </p>

      <div className="mt-6 border border-border rounded-sm p-4">
        <p className="label-caps mb-2">Connector URL</p>
        <div className="flex items-center gap-3">
          <code className="text-sm break-all" data-testid="mcp-url">
            {mcpUrl || "—"}
          </code>
          <button
            data-testid="copy-mcp-url-btn"
            onClick={() => {
              navigator.clipboard?.writeText(mcpUrl);
              toast.success("Connector URL copied");
            }}
            className="h-9 w-9 shrink-0 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-6 text-sm text-muted-foreground space-y-2">
        <p className="label-caps text-foreground">In Claude.ai or Claude Desktop</p>
        <p>Settings → Connectors → Add custom connector → paste the URL → Connect → Allow.</p>
        <p className="label-caps text-foreground pt-2">In Claude Code</p>
        <code className="block bg-secondary border border-border rounded-sm p-3 text-xs break-all">
          claude mcp add --transport http chambers-diary {mcpUrl}
        </code>
      </div>

      <div className="mt-6">
        <p className="label-caps mb-3">Connected</p>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (data?.connections || []).length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="mcp-no-connections">
            Nothing connected yet.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {data.connections.map((c) => (
              <div
                key={c.client_id}
                data-testid={`mcp-connection-${c.client_id}`}
                className="py-4 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-base font-semibold truncate">{c.client_name}</p>
                  <p className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" />
                    {c.scopes.includes("diary:write") ? "Read and write" : "Read only"}
                  </p>
                </div>
                <button
                  data-testid={`mcp-disconnect-${c.client_id}`}
                  onClick={() => disconnect(c.client_id)}
                  className="h-10 w-10 shrink-0 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
